/** The Review step's other cards (DESIGN §6, §8): the risk dashboard and the thesis and reasons collapsed to one row each with Edit. */
import type { KpiSnapshot, PreviewResult, PreviewTrace } from "@bsim/domain";
import { plainDecisionLabel } from "../labels";
import { GxLoading } from "../../theme/GxLoading";
import { GxSkeletonBlock } from "../../theme/primitives";
import type { RiskProjection } from "../../api/riskFields";
import { GxForecastPill, FORECAST_CARD } from "../../theme/ForecastPill";
import { RiskTable } from "../../theme/RiskTable";
import { usePreviewContext } from "../../shell/PreviewProvider";
import { riskModelOf, type MemoItem, type RiskArgs } from "../riskRows";
import type { PreviewLimits } from "../previewRows";
import { withRiskProjection } from "../previewRows";
import type { PlanSummary } from "./planSummary";
import { GxChangeLines } from "../rationale/ChangeLines";

type Items = readonly MemoItem[];

/** The dashboard's model of the plan on the Review step: the card and the memo receipt both read it, so they count the same flags. */
export function reviewRiskModel({ result, limits, lastYear, projection, items, trace, playerChanged, supervisory, rateMove }: {
  readonly rateMove?: RiskArgs["rateMove"];
  readonly result: PreviewResult; readonly limits: PreviewLimits; readonly lastYear: KpiSnapshot; readonly projection: RiskProjection | undefined; readonly items: Items;
  readonly trace: PreviewTrace | null; readonly playerChanged: boolean; readonly supervisory: Parameters<typeof riskModelOf>[0]["supervisory"];
}) {
  return riskModelOf({ result: withRiskProjection(result, projection), limits, lastYear, projection, items, trace, committed: true, decisionLabel: plainDecisionLabel, locked: () => false, playerChanged, supervisory, rateMove });
}

/** A card's place before its figures arrive: the card's own title, a few quiet bars, and the height of what replaces it, so the page does not grow when the figures land. */
export function ReviewPending({ title, height, rows, words, testId, note }: { readonly title: string; readonly height: number; readonly rows: number; readonly words: string; readonly testId: string; readonly note?: string }) {
  return (
    <section className="gx-card gx-rv-card gx-rv-pending" aria-busy="true" data-testid={testId}>
      <header className="gx-rv-h"><h2 className="gx-h2">{title}</h2></header>
      <GxLoading variant="inline" words={words} />
      {note === undefined ? null : <p className="gx-rv-pending-note" data-testid={`${testId}.note`}>{note}</p>}
      <div className="gx-rv-pending-rows"><GxSkeletonBlock height={height - PENDING_CHROME} lines={rows} /></div>
    </section>
  );
}
/** What the wait for the risk officer's note is for: Submit stays locked until the note is opened (`SoloReview`: "Read the note"). */
export const MEMO_PENDING_NOTE = "The risk officer\u2019s note is being prepared; Submit opens once you have read it.";
/** The card's own title and the waiting words take this much of the card's height; the block reserves the rest. */
const PENDING_CHROME = 80;
/** The room the plan table and the risk dashboard take once their figures are in (measured on the finished page). */
export const PLAN_PENDING = { height: 280, rows: 4 } as const;
export const RISK_PENDING = { height: 760, rows: 8 } as const;
export const planPending = <ReviewPending title="What your plan does" {...PLAN_PENDING} words="Working out what your plan does…" testId="review.plan.pending" />;
export const riskPending = <ReviewPending title="Risk dashboard" {...RISK_PENDING} words="Checking your plan against your limits…" testId="review.risk.pending" />;

export function RiskCard({ result, limits, lastYear, projection, items, trace, onDriver, playerChanged, rateMove }: {
  readonly rateMove?: RiskArgs["rateMove"];
  readonly result: PreviewResult | null; readonly limits: PreviewLimits; readonly lastYear: KpiSnapshot; readonly projection: RiskProjection | undefined; readonly items: Items;
  readonly trace: PreviewTrace | null; readonly onDriver: (decisionPath: string) => void; readonly playerChanged: boolean;
}) {
  const supervisory = usePreviewContext()?.params.supervisory;
  if (result === null) return riskPending;
  const model = reviewRiskModel({ result, limits, lastYear, projection, items, trace, playerChanged, supervisory, rateMove });
  return (
    <section className={`gx-card ${FORECAST_CARD} gx-rv-card`} data-testid="review.risk">
      <header className="gx-rv-h"><h2 className="gx-h2">Risk dashboard</h2><GxForecastPill basis="worst quarter-end of the year" /></header>
      <RiskTable model={model} onDriver={onDriver} />
    </section>
  );
}

/** Thesis and each desk's reason, one row each, with the way back to edit it. */
export function ReasonsCard({ summary, onEdit, changes }: { readonly summary: PlanSummary; readonly onEdit: (family: string | null) => void;
  /** Each desk's change lines (`changeLines`), shown above its reason. */ readonly changes?: (family: string) => readonly string[] }) {
  if (summary.rationales.length === 0 && summary.thesis === null) return null;
  return (
    <section className="gx-card gx-rv-card" data-testid="review.reasons">
      <header className="gx-rv-h"><h2 className="gx-h2">Your reasons</h2></header>
      <ul className="gx-rv-rows">
        {summary.thesis === null ? null : <li data-testid="decide.plan.why.thesis"><span><b>Thesis</b>{" · "}{summary.thesis}</span><button type="button" className="gx-linkrow" onClick={() => onEdit(null)}>Edit</button></li>}
        {summary.rationales.map((r) => <li key={r.family} data-testid="decide.plan.why"><span><b>{r.label}</b>{" · "}{r.aim}{" · "}{r.text}{(changes?.(r.family) ?? []).length === 0 ? null : <GxChangeLines testId={`review.reasons.${r.family}.changes`} lines={changes?.(r.family) ?? []} />}</span><button type="button" className="gx-linkrow" onClick={() => onEdit(r.family)}>Edit</button></li>)}
      </ul>
    </section>
  );
}
